import React, { useState, useEffect } from 'react';

const ProfilePage = ({ userId }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [toast, setToast] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);

  const csrf = () => document.querySelector('[name="csrf-token"]')?.content;

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    try {
      const res = await fetch('/api/v1/auth/me', {
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() }
      });
      const data = await res.json();
      if (data.success) {
        const u = data.data?.user || data.user;
        setUser(u);
        setForm({
          first_name: u.first_name || '',
          last_name: u.last_name || '',
          nickname: u.nickname || '',
          phone_number: u.phone_number || '',
          birthdate: u.birthdate || '',
          address: u.address || '',
          zip_code: u.zip_code || '',
          city: u.city || '',
          country: u.country || '',
          bio: u.bio || '',
          linkedin_url: u.linkedin_url || '',
          website_url: u.website_url || '',
        });
      }
    } catch (e) {
      console.error(e);
      showToast('Erreur lors du chargement du profil', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch('/api/v1/auth/update_profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() },
        body: JSON.stringify({ user: form })
      });
      const data = await res.json();
      if (data.success) {
        const u = data.data?.user || data.user;
        if (u) setUser(u);
        setEditing(false);
        showToast(data.message || 'Profil mis à jour');
      } else {
        showToast(data.error || 'Erreur lors de la sauvegarde', 'error');
      }
    } catch (e) {
      showToast('Erreur réseau', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      showToast('Les mots de passe ne correspondent pas', 'error');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      showToast('Le mot de passe doit contenir au moins 6 caractères', 'error');
      return;
    }
    setChangingPassword(true);
    try {
      const res = await fetch('/api/v1/auth/change_password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() },
        body: JSON.stringify({ auth: { current_password: passwordForm.current_password, new_password: passwordForm.new_password } })
      });
      const data = await res.json();
      if (data.success) {
        showToast(data.message || 'Mot de passe modifié');
        setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
        setShowPasswordSection(false);
      } else {
        showToast(data.error || 'Erreur', 'error');
      }
    } catch (e) {
      showToast('Erreur réseau', 'error');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showToast('L\'image ne doit pas dépasser 5 Mo', 'error');
      return;
    }
    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      const res = await fetch(`/api/v1/users/${userId}`, {
        method: 'PUT',
        headers: { 'X-CSRF-Token': csrf() },
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        const u = data.data?.user || data.user;
        if (u?.avatar_url) setUser(prev => ({ ...prev, avatar_url: u.avatar_url }));
        showToast('Photo de profil mise à jour');
      } else {
        showToast('Erreur lors de l\'upload', 'error');
      }
    } catch (e) {
      showToast('Erreur réseau', 'error');
    } finally {
      setAvatarUploading(false);
    }
  };

  // --- Styles ---
  const colors = {
    primary: '#7c2837', primaryDark: '#5a1d28', primaryLight: '#a8384e',
    bg: '#fdf8f9', card: '#ffffff', text: '#2c0e16', textMuted: '#6c4a53',
    border: '#e8d0d6', success: '#28a745', warning: '#ffc107', danger: '#dc3545', info: '#17a2b8',
  };

  const containerStyle = {
    maxWidth: 900, margin: '0 auto', padding: '30px 20px',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", color: colors.text, minHeight: '80vh',
  };

  const headerStyle = {
    background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
    color: '#fff', padding: '35px 30px', borderRadius: 16, marginBottom: 30,
    display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap',
  };

  const cardStyle = {
    background: colors.card, borderRadius: 12, padding: 25,
    boxShadow: '0 2px 12px rgba(124,40,55,0.08)', border: `1px solid ${colors.border}`, marginBottom: 20,
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 8,
    border: `1px solid ${colors.border}`, fontSize: 14, boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  };

  const labelStyle = {
    display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 13, color: colors.textMuted,
  };

  const fieldGroupStyle = {
    marginBottom: 16,
  };

  const btnStyle = (variant = 'primary') => {
    const map = {
      primary: { bg: colors.primary, color: '#fff' },
      success: { bg: colors.success, color: '#fff' },
      danger: { bg: colors.danger, color: '#fff' },
      outline: { bg: 'transparent', color: colors.primary, border: `1px solid ${colors.primary}` },
    };
    const v = map[variant] || map.primary;
    return {
      padding: '10px 20px', borderRadius: 8, border: v.border || 'none',
      background: v.bg, color: v.color, cursor: 'pointer', fontSize: 14, fontWeight: 600, transition: 'all 0.2s',
    };
  };

  const badgeStyle = (color) => ({
    display: 'inline-block', padding: '4px 12px', borderRadius: 12,
    fontSize: 12, fontWeight: 600, color: '#fff', background: color,
  });

  const toastStyle = {
    position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '14px 24px', borderRadius: 10,
    color: '#fff', fontWeight: 600, boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
    background: toast?.type === 'error' ? colors.danger : colors.success,
  };

  const avatarStyle = {
    width: 80, height: 80, borderRadius: '50%', objectFit: 'cover',
    border: '3px solid rgba(255,255,255,0.5)',
  };

  const avatarPlaceholder = {
    width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32,
    border: '3px solid rgba(255,255,255,0.5)', color: '#fff',
  };

  const levelLabels = {
    invite: 'Invité', adherent_sympathisant: 'Sympathisant', adherent_participant: 'Participant',
    adherent_actif: 'Actif', membre_fondateur: 'Fondateur',
  };

  const statusLabels = {
    pending: 'En attente', active: 'Actif', inactive: 'Inactif', suspended: 'Suspendu',
  };

  // --- Render helpers ---
  const renderField = (label, field, type = 'text', options = {}) => {
    const value = editing ? form[field] : (user?.[field] || '');
    return (
      <div style={fieldGroupStyle}>
        <label style={labelStyle}>{label}</label>
        {editing ? (
          type === 'textarea' ? (
            <textarea
              value={value} onChange={e => handleChange(field, e.target.value)}
              style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
              placeholder={options.placeholder || ''}
            />
          ) : (
            <input
              type={type} value={value} onChange={e => handleChange(field, e.target.value)}
              style={inputStyle} placeholder={options.placeholder || ''}
            />
          )
        ) : (
          <div style={{ padding: '10px 0', fontSize: 15, color: value ? colors.text : colors.textMuted }}>
            {value || '—'}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ ...containerStyle, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
          <p style={{ color: colors.textMuted, fontSize: 16 }}>Chargement du profil...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={containerStyle}>
        <div style={{ ...cardStyle, textAlign: 'center', padding: 50 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
          <p style={{ fontSize: 16 }}>Impossible de charger le profil</p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {toast && <div style={toastStyle}>{toast.message}</div>}

      {/* Header with avatar */}
      <div style={headerStyle}>
        <div style={{ position: 'relative' }}>
          {user.avatar_url ? (
            <img src={user.avatar_url} alt="Avatar" style={avatarStyle} />
          ) : (
            <div style={avatarPlaceholder}>
              {(user.first_name || '?')[0]?.toUpperCase()}
            </div>
          )}
          <label style={{
            position: 'absolute', bottom: -4, right: -4, width: 28, height: 28, borderRadius: '50%',
            background: colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', border: '2px solid #fff', fontSize: 14,
          }}>
            📷
            <input type="file" accept="image/*" onChange={handleAvatarUpload}
              style={{ display: 'none' }} disabled={avatarUploading} />
          </label>
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 26 }}>{user.first_name} {user.last_name}</h1>
          {user.nickname && <p style={{ margin: '4px 0 0', opacity: 0.8, fontSize: 14 }}>"{user.nickname}"</p>}
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <span style={badgeStyle(colors.success)}>{statusLabels[user.status] || user.status}</span>
            <span style={badgeStyle('rgba(255,255,255,0.25)')}>{levelLabels[user.level] || user.level}</span>
          </div>
          <p style={{ margin: '8px 0 0', opacity: 0.6, fontSize: 13 }}>{user.email}</p>
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        {editing ? (
          <>
            <button onClick={handleSave} disabled={saving} style={btnStyle('success')}>
              {saving ? '⏳ Sauvegarde...' : '✅ Enregistrer'}
            </button>
            <button onClick={() => { setEditing(false); loadProfile(); }} style={btnStyle('outline')}>
              Annuler
            </button>
          </>
        ) : (
          <button onClick={() => setEditing(true)} style={btnStyle('primary')}>
            ✏️ Modifier mes informations
          </button>
        )}
      </div>

      {/* Personal info */}
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, color: colors.primary, borderBottom: `2px solid ${colors.border}`, paddingBottom: 10 }}>
          👤 Informations personnelles
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0 24px' }}>
          {renderField('Prénom', 'first_name')}
          {renderField('Nom', 'last_name')}
          {renderField('Surnom', 'nickname', 'text', { placeholder: 'Optionnel' })}
          {renderField('Date de naissance', 'birthdate', 'date')}
          {renderField('Téléphone', 'phone_number', 'tel')}
        </div>
      </div>

      {/* Address */}
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, color: colors.primary, borderBottom: `2px solid ${colors.border}`, paddingBottom: 10 }}>
          📍 Adresse
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0 24px' }}>
          {renderField('Adresse', 'address')}
          {renderField('Code postal', 'zip_code')}
          {renderField('Ville', 'city')}
          {renderField('Pays', 'country')}
        </div>
      </div>

      {/* Bio & links */}
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, color: colors.primary, borderBottom: `2px solid ${colors.border}`, paddingBottom: 10 }}>
          📝 Bio & Liens
        </h3>
        {renderField('Bio', 'bio', 'textarea', { placeholder: 'Présentez-vous en quelques mots...' })}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0 24px' }}>
          {renderField('LinkedIn', 'linkedin_url', 'url', { placeholder: 'https://linkedin.com/in/...' })}
          {renderField('Site web', 'website_url', 'url', { placeholder: 'https://...' })}
        </div>
      </div>

      {/* Password section */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => setShowPasswordSection(!showPasswordSection)}>
          <h3 style={{ marginTop: 0, marginBottom: 0, color: colors.primary }}>
            🔒 Mot de passe
          </h3>
          <span style={{ fontSize: 18, color: colors.textMuted }}>{showPasswordSection ? '▲' : '▼'}</span>
        </div>
        {showPasswordSection && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${colors.border}` }}>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Mot de passe actuel</label>
              <input type="password" value={passwordForm.current_password}
                onChange={e => setPasswordForm(p => ({ ...p, current_password: e.target.value }))}
                style={inputStyle} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0 24px' }}>
              <div style={fieldGroupStyle}>
                <label style={labelStyle}>Nouveau mot de passe</label>
                <input type="password" value={passwordForm.new_password}
                  onChange={e => setPasswordForm(p => ({ ...p, new_password: e.target.value }))}
                  style={inputStyle} />
              </div>
              <div style={fieldGroupStyle}>
                <label style={labelStyle}>Confirmer le mot de passe</label>
                <input type="password" value={passwordForm.confirm_password}
                  onChange={e => setPasswordForm(p => ({ ...p, confirm_password: e.target.value }))}
                  style={inputStyle} />
              </div>
            </div>
            <button onClick={handleChangePassword} disabled={changingPassword} style={btnStyle('primary')}>
              {changingPassword ? '⏳ Modification...' : '🔑 Changer le mot de passe'}
            </button>
          </div>
        )}
      </div>

      {/* Account info (read-only) */}
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, color: colors.primary, borderBottom: `2px solid ${colors.border}`, paddingBottom: 10 }}>
          ℹ️ Informations du compte
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0 24px' }}>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Email</label>
            <div style={{ padding: '10px 0', fontSize: 15 }}>{user.email}</div>
          </div>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Niveau</label>
            <div style={{ padding: '10px 0' }}>
              <span style={badgeStyle(colors.primary)}>{levelLabels[user.level] || user.level}</span>
            </div>
          </div>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Statut</label>
            <div style={{ padding: '10px 0' }}>
              <span style={badgeStyle(user.status === 'active' ? colors.success : colors.warning)}>
                {statusLabels[user.status] || user.status}
              </span>
            </div>
          </div>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Membre depuis</label>
            <div style={{ padding: '10px 0', fontSize: 15 }}>
              {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
